import _ from 'lodash';
import React, { useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { SetRequired } from 'type-fest';
import styles from './TextWithIcon.module.scss';

const FLOAT_CONTAINER_BOTTOM_OFFSET = parseInt(
  styles.floatContainerBottomOffset,
  10,
);

const getRangeForNode = (node: Node) => {
  const range = document.createRange();
  range.selectNode(node);
  return range;
};

interface WrapGroupProps {
  text: React.ReactNode;
  icon: React.ReactNode;
  iconPosition: 'start' | 'end';
  noWrap?: boolean;
}
const WrapGroup = ({ text, icon, iconPosition, noWrap }: WrapGroupProps) => {
  let leadingComponent = iconPosition === 'start' ? icon : text;
  let trailingComponent = iconPosition === 'start' ? text : icon;

  if (typeof text === 'string') {
    if (iconPosition === 'start') {
      const [first, ...remaining] = _.split(text, '');
      const rest = remaining.join('');
      leadingComponent = (
        <span className={styles.wrappingGroup}>
          {icon}
          {first}
        </span>
      );
      trailingComponent = rest;
    } else {
      const chars = _.split(text, '');
      const rest = chars.slice(0, chars.length - 1).join('');
      const last = chars[chars.length - 1];
      leadingComponent = rest;
      trailingComponent = (
        <span className={styles.wrappingGroup}>
          {last}
          {icon}
        </span>
      );
    }
  }

  return (
    <span className={noWrap ? styles.wrappingGroup : styles.wrap}>
      {leadingComponent}
      {trailingComponent}
    </span>
  );
};

interface UseTruncatedLastRowOptions {
  containerRef: React.RefObject<HTMLSpanElement>;
  skip: boolean;
}

const useTruncatedLastRowRect = ({
  containerRef,
  skip,
}: UseTruncatedLastRowOptions) => {
  const [truncatedLastRowRect, setTruncatedLastRowRect] =
    useState<DOMRect | null>(null);

  useLayoutEffect(() => {
    const containerEl = containerRef.current;
    if (!containerEl || skip) return undefined;

    const updateLastRangeRect = () => {
      const { height } = containerEl.getBoundingClientRect();
      const isTruncated =
        height < containerEl.scrollHeight - FLOAT_CONTAINER_BOTTOM_OFFSET;

      if (isTruncated) {
        const rangeRects = getRangeForNode(containerEl).getClientRects();
        const containerRect = containerEl.getBoundingClientRect();
        const visibleRects = Array.from(rangeRects).filter(
          (rect) => rect.bottom <= containerRect.bottom,
        );

        setTruncatedLastRowRect(_.last(visibleRects) ?? null);
      } else {
        setTruncatedLastRowRect(null);
      }
    };

    updateLastRangeRect();

    const resizeObserver = new ResizeObserver(() => updateLastRangeRect());

    resizeObserver.observe(containerEl);

    return () => {
      resizeObserver.disconnect();
    };
  }, [containerRef, skip]);

  return truncatedLastRowRect;
};

interface TruncateContentProps {
  iconPosition: 'start' | 'end';
  icon?: React.ReactNode;
  text: React.ReactNode;
}

const MultiLineTruncateContent = ({
  iconPosition,
  icon,
  text,
}: TruncateContentProps) => {
  const iconRef = useRef<HTMLSpanElement | null>(null);
  const containerRef = useRef<HTMLSpanElement>(null);

  const truncatedLastRowRect = useTruncatedLastRowRect({
    containerRef,
    skip: icon == null || iconPosition !== 'end',
  });

  if (iconPosition === 'start' || !icon) {
    return (
      <>
        {icon}
        <span className={styles.multiLine}>{text}</span>
      </>
    );
  }

  return (
    <span ref={containerRef} className={styles.multiLine}>
      {truncatedLastRowRect ? (
        <>
          <span
            className={styles.floatContainer}
            style={
              {
                '--truncated-last-row-rect-height': `${truncatedLastRowRect.height}px`,
              } as React.CSSProperties
            }
          >
            <span className={styles.iconContainer} ref={iconRef}>
              {icon}
            </span>
          </span>
          {text}
        </>
      ) : (
        <WrapGroup
          text={text}
          icon={
            <span className={styles.iconContainer} ref={iconRef}>
              {icon}
            </span>
          }
          iconPosition={iconPosition}
        />
      )}
    </span>
  );
};

const SingeLineTruncateContent = ({
  iconPosition,
  icon,
  text,
}: TruncateContentProps) => {
  return (
    <>
      {iconPosition === 'start' && icon}
      <span className={styles.singleLine}>{text}</span>
      {iconPosition === 'end' && icon}
    </>
  );
};

interface TextWithIconProps {
  text: React.ReactNode;
  icon?: React.ReactNode;
  iconPosition?: 'start' | 'end';
  noWrap?: boolean;
  truncate?: 'single' | 'multi';
  to?: string;
}

const TruncatedTextWithIcon = ({
  text,
  icon,
  iconPosition = 'end',
  truncate,
  to,
}: SetRequired<Omit<TextWithIconProps, 'noWrap'>, 'truncate'>) => {
  const props: TruncateContentProps = {
    iconPosition,
    icon,
    text,
  };

  const content =
    truncate === 'single' ? (
      <SingeLineTruncateContent {...props} />
    ) : (
      <MultiLineTruncateContent {...props} />
    );

  return (
    <div className={styles.truncate}>
      {to ? <Link to={to}>{content}</Link> : content}
    </div>
  );
};

const TextWithIcon = ({
  text,
  icon,
  iconPosition = 'end',
  noWrap,
  truncate,
  to,
}: TextWithIconProps) => {
  if (truncate) {
    return (
      <TruncatedTextWithIcon {...{ text, icon, iconPosition, truncate, to }} />
    );
  }

  return (
    <WrapGroup
      text={text}
      icon={icon}
      iconPosition={iconPosition}
      noWrap={noWrap}
    />
  );
};

export default TextWithIcon;
